import { useForm } from "react-hook-form";
import Partners from "@/components/Partners";
import PaymentPartners from "@/components/PaymentPartners";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { insertBookingSchema, type InsertBooking } from "@shared/schema";
import { bookingPartners } from "@/data/partnersData";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import ParallaxBackground from "@/components/ParallaxBackground";
import { captureLead, trackFormStart } from "@/lib/analytics";

export default function Booking() {
  const { toast } = useToast();

  const form = useForm<InsertBooking>({
    resolver: zodResolver(insertBookingSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      participants: 1,
      tourDate: new Date(), // Use Date object
      message: "",
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: InsertBooking) => {
      // 1. Format the email payload for Web3Forms
      const web3formsPayload = {
        access_key: "ee06da99-1582-4a82-80e6-e7196b072777",
        subject: `New Safari Booking: ${data.name}`,
        from_name: data.name,
        email: data.email,
        phone: data.phone || "Not provided",
        date_requested: new Date(data.tourDate).toLocaleDateString(),
        number_of_guests: data.participants,
        additional_details: data.message || "None",
      };

      // 2. Send the HTTP request cleanly to Web3Forms to bypass your server
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(web3formsPayload),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || "Failed to submit booking");
      }

      return json;
    },
    onSuccess: () => {
      toast({
        title: "Booking Submitted Successfully!",
        description: `We've received your booking request and will contact you shortly to confirm your safari tour.`,
      });
      form.reset();
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Submission Error",
        description: error.message,
      });
    },
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="relative py-24 bg-gray-900 overflow-hidden">
        <ParallaxBackground intensity={3} variant="dark" className="opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold text-white mb-6"
          >
            Book Your Safari
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-gray-300 max-w-2xl mx-auto"
          >
            Start your African adventure by filling out the booking form below
          </motion.p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 -mt-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/95 backdrop-blur-xl p-8 sm:p-12 rounded-3xl shadow-2xl border border-white/50"
        >
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
              className="space-y-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">Full Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          onFocus={() => trackFormStart('Booking Form')}
                          className="h-12 bg-slate-50 border-slate-200 focus:border-slate-400 focus:bg-white transition-all rounded-lg"
                          placeholder="e.g. John Doe"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">Email Address</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} className="h-12 bg-slate-50 border-slate-200 focus:border-slate-400 focus:bg-white transition-all rounded-lg" placeholder="john@example.com" />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone Number</FormLabel>
                      <FormControl>
                        <Input {...field} className="h-12 bg-slate-50 border-slate-200 focus:border-slate-400 focus:bg-white transition-all rounded-lg" placeholder="+1 (555) 000-0000" />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tourDate"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">Preferred Date</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          value={
                            field.value
                              ? new Date(field.value).toISOString().split("T")[0]
                              : ""
                          }
                          onChange={(e) =>
                            field.onChange(new Date(e.target.value))
                          }
                          className="h-12 bg-slate-50 border-slate-200 focus:border-slate-400 focus:bg-white transition-all rounded-lg"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="participants"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Number of Guests
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value))
                        }
                        className="h-12 bg-slate-50 border-slate-200 focus:border-slate-400 focus:bg-white transition-all rounded-lg"
                        min={1}
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Additional Details
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="min-h-[120px] bg-slate-50 border-slate-200 focus:border-slate-400 focus:bg-white transition-all rounded-lg resize-none p-4"
                        placeholder="Tell us about your preferences, special requirements, or specific tours you're interested in..."
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full bg-slate-900 hover:bg-black text-white h-14 text-lg rounded-xl shadow-xl transition-all hover:scale-[1.01]"
                disabled={mutation.isPending}
              >
                {mutation.isPending ? "Processing Request..." : "Secure Your Booking"}
              </Button>
            </form>
          </Form>
        </motion.div>
      </div>

      {/* Display booking partners (airline partners) */}
      {/* <Partners 
        partners={bookingPartners} 
        title="Our Airline Partners"
      /> */}

      {/* Display payment options */}
      <PaymentPartners />
    </div>
  );
}
